/**
 * What a transcript LOOKS like to a reader, and where a record item's claim
 * came from (#13870, Wave 5 of #13791's design canvas).
 *
 * The pure half of two surfaces that are really one thing: the transcript
 * view at `/meetings/[id]/transcript`, and the provenance popover on the
 * record rail that opens into it. Ordering, the around-a-timestamp window,
 * the link between them, and which rows may cite a transcript at all — no
 * Prisma, no React, no `server-only`, so the rail can call it during render
 * and a test can assert it against literal spans.
 *
 * ## No speaker, ever
 *
 * {@link TranscriptViewSegment} carries no speaker field and must not grow
 * one. `transcript_segments.speakerLabel` exists and is NULL for every row
 * this product has ever written — there is no diarization — so a surface
 * that rendered it would be rendering an absence, and one that substituted
 * a name for the absence would be inventing attribution. #13869 removed
 * exactly that mistake from the record rail; the transcript is the surface
 * where it would be most convincing and therefore worst.
 *
 * ## A moment is HALF-OPEN, and a gap belongs to the line before it
 *
 * Two adjacent spans share a millisecond: `s1.endMs === s2.startMs`. Under
 * `[start, end)` exactly one of them claims it, so "the segment at 4000" has
 * one answer rather than two. And a provider leaves GAPS — silence, a span it
 * dropped — so an offset that falls in one is attributed to the last thing
 * actually said before it rather than to the next thing said after, which had
 * not been said yet.
 *
 * An offset BEFORE the first span matches nothing. That is the honest answer:
 * there is no line to show, and showing the first line instead would tell a
 * reader that a claim came from words spoken later than it.
 */

import type { SpanCertainty } from '@/lib/notetaker/transcript-confidence'

import type {
  MeetingRecordItem,
  MeetingRecordItemSource,
} from '@/lib/meetings/record-item-model'

/**
 * One turn of transcript as a surface reads it.
 *
 * `certainty` is DERIVED at read time by `spanCertainty` and is three-valued
 * on purpose — `unknown` is not `confident`. Every span written before
 * #13811 is `unknown` forever, so a view that drew unknown as confident would
 * promise a district that a number nobody checked is right.
 */
export interface TranscriptViewSegment {
  id: string
  startMs: number
  endMs: number
  text: string
  /**
   * The stored number itself, so the C7 inspector can print *"confidence
   * 0.68"* (#13812).
   *
   * Carried ALONGSIDE `certainty` rather than instead of it. The verdict is
   * the thing a renderer must act on — re-deriving it from this number at a
   * call site is exactly the NULL-coercion mistake `transcript-confidence.ts`
   * exists to prevent — but a reader being asked to check a span is owed the
   * measurement, not just the conclusion drawn from it. NULL means unknown
   * here for the same reason it does in the column.
   */
  confidence: number | null
  certainty: SpanCertainty
  /**
   * A person's edit of these words, or null when nobody has corrected them
   * (#13812).
   *
   * Its PRESENCE is what makes the span stop being flagged — see
   * `isSpanFlaggedUncertain` in `transcript-uncertain-spans.ts`. Optional at
   * the type level so the record rail's provenance window, which neither
   * renders nor cares about corrections, can keep building segments without
   * a second read.
   */
  correction?: TranscriptSpanCorrection | null
}

/**
 * Who corrected a span, and when (#13812).
 *
 * A correction is provenance-bearing: the transcript stops being purely
 * machine output at that point and the record must be able to say so. The
 * durable record of the edit is a `FieldProvenance` row plus its append-only
 * `SyncEvent` (ADR-0030); this is the projection of it a surface renders.
 *
 * `correctedByName` is nullable because the person may since have been
 * deleted. An anonymous "this was corrected" is still a true and useful
 * statement, and is strictly better than suppressing the fact of the edit.
 */
export interface TranscriptSpanCorrection {
  correctedByName: string | null
  correctedAt: Date
}

/** The search parameter that carries a moment into the transcript view. */
export const TRANSCRIPT_MOMENT_PARAM = 'at'

/**
 * The search parameter that names WHICH recording a moment is on (#14427).
 *
 * A note recorded more than once has one timeline per recording — a
 * segment's `startMs` is relative to its own recording's start — so a bare
 * `?at=` is ambiguous past part 1. It rides beside {@link
 * TRANSCRIPT_MOMENT_PARAM}; a moment without it resolves in part 1, which is
 * what every link minted before parts existed meant.
 */
export const TRANSCRIPT_RECORDING_PARAM = 'recording'

/** A cuid is ~25 chars; anything this long is not a recording id. */
const MAX_RECORDING_PARAM_LENGTH = 200

/**
 * The search parameter that carries a cited SEGMENT into the transcript view
 * (#14378) — a note summary claim's citation. A segment id rather than a
 * moment because a claim cites segments, and two segments can share a start.
 */
export const TRANSCRIPT_SEGMENT_PARAM = 'segment'

/**
 * The DOM id a transcript segment carries, so a citation can land on it:
 * `seg-<segmentId>`.
 */
export function transcriptSegmentAnchor(segmentId: string): string {
  return `seg-${segmentId}`
}

/**
 * The `?segment=` value back out of a URL, or `null` — refusing a repeated
 * parameter for {@link parseTranscriptMoment}'s reason.
 */
export function parseTranscriptSegmentParam(
  raw: string | string[] | undefined,
): string | null {
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  return trimmed.length === 0 ? null : trimmed
}

/**
 * The one total order a transcript is read in: `startMs`, then `id`.
 *
 * The tiebreak is not defensive. Two spans can share a `startMs` — an
 * overlapping utterance, or a zero-length span — and `startMs` alone leaves
 * their relative order to whatever produced the list, so the same transcript
 * could render two ways on two loads. `id` is the column that exists and is
 * unique; `transcript-reader.ts` orders its query the same way, so the pure
 * order and the SQL order are the same order rather than two that agree by
 * coincidence.
 */
export function compareTranscriptSegments(
  a: TranscriptViewSegment,
  b: TranscriptViewSegment,
): number {
  if (a.startMs !== b.startMs) return a.startMs - b.startMs
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

/** The same segments in that order. Copies; the caller's array is left alone. */
export function orderTranscriptSegments(
  segments: readonly TranscriptViewSegment[],
): TranscriptViewSegment[] {
  return [...segments].sort(compareTranscriptSegments)
}

/**
 * The segment a millisecond offset belongs to, or `null`.
 *
 * The containing span under `[startMs, endMs)`; failing that, the last span
 * that had already started. `null` only when the offset precedes every span
 * (or the transcript is empty, or the offset is not a real position) — see
 * the module header for why that case invents nothing.
 *
 * Orders the input itself rather than trusting the caller, so the answer does
 * not depend on how the list arrived.
 */
export function findTranscriptSegmentAt(
  segments: readonly TranscriptViewSegment[],
  ms: number,
): TranscriptViewSegment | null {
  if (!Number.isFinite(ms) || ms < 0) return null

  let preceding: TranscriptViewSegment | null = null
  for (const segment of orderTranscriptSegments(segments)) {
    if (ms >= segment.startMs && ms < segment.endMs) return segment
    // Ordered, so the last span that started at or before `ms` is the one
    // still held here when the loop passes the offset.
    if (segment.startMs <= ms) preceding = segment
  }
  return preceding
}

/**
 * A line of transcript with the turn either side of it.
 *
 * `before` / `after` are `null` at the ends of the transcript — a real answer
 * rather than a missing one, and never a wrapped-around neighbour.
 */
export interface TranscriptMoment {
  before: TranscriptViewSegment | null
  at: TranscriptViewSegment
  after: TranscriptViewSegment | null
}

/** The window around an offset, or `null` when nothing matches it. */
export function transcriptMomentAt(
  segments: readonly TranscriptViewSegment[],
  ms: number,
): TranscriptMoment | null {
  const ordered = orderTranscriptSegments(segments)
  const at = findTranscriptSegmentAt(ordered, ms)
  if (at === null) return null

  const index = ordered.findIndex(segment => segment.id === at.id)
  return {
    before: index > 0 ? (ordered[index - 1] ?? null) : null,
    at,
    after: index < ordered.length - 1 ? (ordered[index + 1] ?? null) : null,
  }
}

/**
 * The `?recording=` value back out of a URL, or `null` — refusing a repeated
 * parameter for {@link parseTranscriptMoment}'s reason, and a value too long
 * to be an id. The page only ever MATCHES it against the recordings it has
 * already read under the reader's org, so an unknown id highlights nothing.
 */
export function parseTranscriptRecordingParam(
  raw: string | string[] | undefined,
): string | null {
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  if (trimmed.length === 0 || trimmed.length > MAX_RECORDING_PARAM_LENGTH) {
    return null
  }
  return trimmed
}

/** The slice of a record item this module needs to decide the affordance. */
export interface TranscriptCitingItem {
  source: MeetingRecordItemSource
  startMs: number | null
}

/**
 * May this row show where it came from?
 *
 * Two conditions, and both are about honesty rather than convenience. A row
 * with no `startMs` has no position in what was said, so there is no line to
 * open a popover onto. A MANUAL row did not come from a transcript at all —
 * a person typed it — and its provenance pill already says so; offering a
 * "here is the line this came from" button on it would be a second,
 * contradictory claim about the same row.
 *
 * Absent rather than disabled: a disabled control says "this could show you
 * something, but not now", and for these rows there is nothing to show ever.
 */
export function itemCanCiteTranscript(item: TranscriptCitingItem): boolean {
  return item.source !== 'MANUAL' && item.startMs !== null
}

/** One item's evidence: the window, plus the offset the link carries. */
export interface RecordItemProvenance extends TranscriptMoment {
  /** The item's own `startMs`, NOT `at.startMs` — the link lands on the
   * moment the item cites, which is inside `at` but need not be its start. */
  startMs: number
  /**
   * The recording whose timeline `startMs` is on (#14427). The link carries
   * it, so the transcript resolves the moment inside that part rather than
   * in part 1.
   */
  recordingId: string
}

/**
 * The provenance window for every row that has one, keyed by item id.
 *
 * Built ONCE over the transcript rather than per row, because the
 * alternative is one query per item on a page that already renders dozens.
 *
 * Each item is placed on its OWN recording's segments (#14427): an item's
 * `startMs` is relative to the recording it was extracted from, so looking
 * it up in any other recording quotes words said at the same offset of a
 * different capture. An item whose recording is not in `segmentsByRecording`
 * — no recording, or one this reader could not read — is OMITTED, as is a row
 * whose offset matches no segment: a button that opens onto nothing (or onto
 * the wrong thing) is worse than no button, and the rail decides whether to
 * render the affordance by asking this map.
 */
export function buildRecordItemProvenance(
  segmentsByRecording: ReadonlyMap<string, readonly TranscriptViewSegment[]>,
  items: readonly Pick<
    MeetingRecordItem,
    'id' | 'source' | 'startMs' | 'recordingId'
  >[],
): Record<string, RecordItemProvenance> {
  if (segmentsByRecording.size === 0) return {}
  const ordered = new Map<string, TranscriptViewSegment[]>()

  const windows: Record<string, RecordItemProvenance> = {}
  for (const item of items) {
    if (!itemCanCiteTranscript(item) || item.startMs === null) continue
    const recordingId = item.recordingId ?? null
    if (recordingId === null) continue
    let segments = ordered.get(recordingId)
    if (segments === undefined) {
      const raw = segmentsByRecording.get(recordingId)
      if (raw === undefined) continue
      segments = orderTranscriptSegments(raw)
      ordered.set(recordingId, segments)
    }
    const moment = transcriptMomentAt(segments, item.startMs)
    if (moment === null) continue
    windows[item.id] = { ...moment, startMs: item.startMs, recordingId }
  }
  return windows
}

/**
 * The `?at=` value back out of a URL, or `null`.
 *
 * REFUSES rather than defaults, including a repeated parameter: `?at=1&at=2`
 * arrives as an array, and picking one of the two would silently distinguish
 * a segment the caller did not ask for. A refused moment renders the
 * transcript with nothing highlighted, which is a correct page.
 */
export function parseTranscriptMoment(
  raw: string | string[] | undefined,
): number | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null
  const value = Number(raw)
  if (!Number.isSafeInteger(value) || value < 0) return null
  return value
}

/**
 * The transcript view, optionally at a moment.
 *
 * A plain string rather than a `Route`: it is consumed by `next/link`, whose
 * typed-routes `Route` does not admit a query string built at runtime, and
 * the one call site casts. Keeping the construction here means the parameter
 * name has exactly one spelling shared with {@link parseTranscriptMoment}.
 *
 * `recordingId` names the recording the moment is on (#14427) — see
 * {@link TRANSCRIPT_RECORDING_PARAM}. It is carried only WITH a moment: on
 * its own it places nothing.
 */
export function transcriptMomentPath(
  meetingId: string,
  startMs: number | null,
  recordingId: string | null = null,
): string {
  const base = `/meetings/${meetingId}/transcript`
  if (startMs === null || !Number.isSafeInteger(startMs) || startMs < 0) {
    return base
  }
  const moment = `${base}?${TRANSCRIPT_MOMENT_PARAM}=${startMs}`
  if (recordingId === null || recordingId.length === 0) return moment
  const recording = new URLSearchParams({
    [TRANSCRIPT_RECORDING_PARAM]: recordingId,
  })
  return `${moment}&${recording.toString()}`
}
